import { Injectable, EventEmitter } from "@angular/core";
import { AbstractControl, FormGroup, ValidationErrors } from "@angular/forms";

import * as semver from "semver";

import { validateAddressList, validateAddress } from "@impulse/sdp-address-parsing";

import { isWebUrl } from "../../util";
import { GatewayView } from "app/administrative/gateways/gateway-view";
import { GatewaysService, GatewayViews } from "app/administrative/gateways";

// Return type any is explicitly added to all methods in order to comply in with the required Type ValidatorFn from angulars validation
@Injectable()
export class ValidationService {
	private activeGateways: GatewayView[] | undefined;

	constructor(gatewayService: GatewaysService) {
		gatewayService.reloadGateways.subscribe((gateways: GatewayViews) => {
			this.activeGateways = gateways.gatewayViews.filter((gateway) => {
				return gateway.state === "active";
			});
		});
	}

	public email(formControl: AbstractControl): any {
		// https://emailregex.com/
		// RFC 5322
		const valid: boolean = /^(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/.test(formControl.value);
		if (!formControl.value || valid) {
			return undefined;
		}
		return { "invalidEmail": true };
	}

	public totpCode(formControl: AbstractControl): any {
		const valid: boolean = /^\d{6}$/.test(formControl.value);
		if (!formControl.value || valid) {
			return undefined;
		}
		return { "invalidTotpCode": true };
	}

	/**
	 * Creates a validator for confirming that passwords match. The resulting validator is intended for the first field where the user
	 * enters the new password.
	 *
	 * @param nameOfPasswordControl the name of the control where the user first enters the new password (should probably be the name of
	 * the the control accepting this validator)
	 * @param nameOfConfirmPasswordControl the name of the control where the user confirms the new password
	 */
	public createConfirmPasswordValidatorForPassword(nameOfPasswordControl: string, nameOfConfirmPasswordControl: string) {
		return (formControl: AbstractControl): any => {
			if (formControl.parent) {
				const pwControl = formControl.parent.get(nameOfPasswordControl);
				const cpwControl = formControl.parent.get(nameOfConfirmPasswordControl);
				if (pwControl && cpwControl) {
					cpwControl.updateValueAndValidity();
				}
			}
			// tslint:disable-next-line:no-null-keyword
			return null;
		};
	}

	/**
	 * Creates a validator for confirming that passwords match. The resulting validator is intended for the field where the user confirms
	 * the new password.
	 *
	 * @param nameOfPasswordControl the name of the control where the user first enters the new password
	 * @param nameOfConfirmPasswordControl the name of the control where the user confirms the new password (should probably be the name
	 * of the control accepting this validator)
	 */
	public createConfirmPasswordValidatorForConfirmPassword(nameOfPasswordControl: string, nameOfConfirmPasswordControl: string) {
		return (formControl: AbstractControl) => {
			if (formControl.parent) {
				const pwControl = formControl.parent.get(nameOfPasswordControl);
				const cpwControl = formControl.parent.get(nameOfConfirmPasswordControl);
				if (pwControl && cpwControl) {
					if (pwControl.value === cpwControl.value) {
						// tslint:disable-next-line:no-null-keyword
						return null;
					}
				}
			}
			return { "confirmNoMatch": true };
		};
	}

	public minMaxPortCheck(formControl: AbstractControl): any {
		const value: string = formControl.value;
		if (!value || value.indexOf("-") < 0) {
			// early exit if no value defined
			return undefined;
		}

		const [min, max] = value.split("-");
		if (Number.parseInt(min, 10) > Number.parseInt(max, 10)) {
			return { "minGtMaxPort": true };
		}
		return undefined;
	}

	public port(formControl: AbstractControl): any {
		const regex = /^([1-9][0-9]{0,3}|[1-5][0-9]{4}|6[0-4][0-9]{3}|65[0-4][0-9]{2}|655[0-2][0-9]|6553[0-5])$/;
		if (!formControl.value) {
			// early exit if no value defined
			return undefined;
		}
		if (!regex.test(formControl.value)) {
			return { "invalidPort": true };
		}
		return undefined;
	}

	public portRange(formControl: AbstractControl): any {
		const regex = /^([1-9][0-9]{0,3}|[1-5][0-9]{4}|6[0-4][0-9]{3}|65[0-4][0-9]{2}|655[0-2][0-9]|6553[0-5])$/;
		if (!formControl.value) {
			// early exit if no value defined
			return undefined;
		}
		const portParts = formControl.value.split("-");
		if (portParts.length > 2) {
			return { "invalidPort": true };
		}
		for (let i = 0; i < portParts.length; i++) {
			if (!regex.test(portParts[i])) {
				return { "invalidPort": true };
			}
		}
		return undefined;
	}

	public singleIp(formControl: AbstractControl): any {
		const value: string = formControl.value;
		if (!!value && !validateAddress(value, "ip")) {
			return { "invalidIp": true };
		}
		return undefined;
	}

	public domainSuffix(formControl: AbstractControl): any {
		const regex = /^([a-zA-Z0-9]([a-zA-Z0-9-_]{0,61}[a-zA-Z0-9])?\.)*[a-zA-Z]{2,63}$/;
		const value: string = formControl.value;
		if (!!value) {
			let subdomain = value;
			if (subdomain.startsWith(".")) {
				subdomain = subdomain.substring(1);
			}
			const m = regex.exec(subdomain);
			if (!m || subdomain.length > 255) {
				return { "invalidDomain": true };
			}
		}
		return undefined;
	}

	public domainOrIpSingle(formControl: AbstractControl): any {
		const value: string = formControl.value;
		if (!!value && !validateAddress(value)) {
			return { "invalidDomainOrIp": true };
		}
		return undefined;
	}

	public domainOrIp(formControl: AbstractControl): any {
		const value: string = formControl.value;
		if (!!value && !validateAddressList(value)) {
			return { "invalidDomainOrIp": true };
		}
		return undefined;
	}

	public webUrl(formControl: AbstractControl): any {
		const value: string = formControl.value;
		if (!!value && !isWebUrl(value)) {
			return { "invalidWebUrl": true };
		}
		return undefined;
	}

	/**
	 * Validator function that is meant for "simple" strings, such as labels.
	 * Valid characters are a-z, A-Z, 0-9, underscores, dashes, and spaces.
	 * @param formControl
	 */
	public simpleStringWithSpaces(formControl: AbstractControl): any {
		if (/^[\w- ]*$/.test(formControl.value)) {
			return undefined;
		} else {
			return { "invalidString": true };
		}
	}

	/**
	 * Generator for a validator function that determines if the form would produce an object that contains a key in common with existing
	 * objects.
	 *
	 * @param objects the list of objects that will be compared against; it is expected that if this validator is being used for an update,
	 * that the object being updated has already been filtered out from this array
	 * @param key the key on the objects that will be checked
	 */
	public duplicate(objects: any[], key: string) {
		return (formControl: AbstractControl): any => {
			const value: string = formControl.value;
			for (const object of objects) {
				if (key in object && object[key] === value) {
					return {
						"duplicate": true
					};
				}
			}
			return undefined;
		};
	}

	public matchesConstant(goodValue: string) {
		return (formControl: AbstractControl): any => {
			const value: string = formControl.value;
			if (goodValue !== value) {
				return {
					"constant": true
				};
			} else {
				return undefined;
			}
		};
	}

	public valuesCannotBetheSame(value1: string, value2: string) {
		return (form: FormGroup): ValidationErrors | undefined => {
			const field1 = form.get(value1);
			const field2 = form.get(value2);
			if (!!field1 && !!field2) {
				if (String(field1.value) === String(field2.value)) {
					return { "valuesAreSame": true };
				}
			}
			return undefined;
		};
	}

	public validateIfEqual(field: string, value: any, validator: (form: FormGroup) => ValidationErrors | undefined) {
		return (form: FormGroup): ValidationErrors | undefined => {
			const ctrl = form.get(field);
			if (!!ctrl && ctrl.value === value) {
				return validator(form);
			}
		};
	}

	/**
	 * @returns true if one or more active gateways are older than the provided version, false if no version is provided or all gateways
	 * are at least that version
	 */
	public isActiveGatewayTooOld(minRequiredVersion: string | undefined): boolean {
		// it's only possible to fail this check if we have at least one active gateway, and we have a minimum valid version
		if (!!this.activeGateways && this.activeGateways.length > 0 && !!minRequiredVersion) {
			for (const gateway of this.activeGateways) {
				// gateway is too old to even have a version number yet, this should be impossible now
				if (!gateway.gatewayVersion) {
					return true;
				}
				// gateway is older than the specified minimum version
				if (semver.lt(gateway.gatewayVersion, minRequiredVersion)) {
					return true;
				}
			}
			// all gateways are at least the specified minimum version
			return false;
		} else {
			// either we're missing gateway data, or missing required version data
			// either way, this is a success state, there is no error possible here
			return false;
		}
	}
}
